import { ArrowRight, CalendarClock, FilePlus2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Card, CardContent } from "@/components/ui/card";

/**
 * Brand-new-tenant dashboard hero. Replaces the financial / salud-
 * fiscal / próximos-vencimientos panels when the tenant has issued
 * zero CFEs ever — those panels assume activity and read as alarms
 * ("Atención requerida 0/100", "Hay cosas para revisar", "IVA estimado
 * 0 UYU") on a clean tenant where nothing is actually wrong.
 *
 * Gender-neutral Spanish — vos verb forms throughout, no
 * masculine/feminine adjectives.
 */
export function DashboardWelcomeEmptyState(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <Card>
      <CardContent className="flex flex-col items-center gap-5 px-6 py-12 text-center sm:px-12">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[color:var(--fx-accent-100)] text-[color:var(--fx-accent-700)]">
          <FilePlus2 className="h-7 w-7" aria-hidden />
        </div>
        <div className="space-y-2">
          <h2 className="text-2xl font-semibold text-[color:var(--fx-ink-900)]">
            {t(
              "dashboard.welcomeEmpty.title",
              "Empezá emitiendo tu primera factura.",
            )}
          </h2>
          <p className="max-w-md text-[15px] text-[color:var(--fx-ink-600)]">
            {t(
              "dashboard.welcomeEmpty.body",
              "Cuando emitas la primera, este panel se llena solo con tu IVA estimado, vencimientos y los movimientos que vayas teniendo.",
            )}
          </p>
        </div>
        <div className="flex flex-col items-center gap-3 sm:flex-row">
          <button
            type="button"
            className="fx-btn fx-btn-primary fx-btn-md"
            onClick={() => navigate("/invoices/new")}
          >
            {t(
              "dashboard.welcomeEmpty.createInvoice",
              "Crear tu primera factura",
            )}
            <ArrowRight className="h-4 w-4" aria-hidden />
          </button>
          <button
            type="button"
            className="fx-btn fx-btn-ghost fx-btn-md"
            onClick={() => navigate("/fiscal-agenda")}
          >
            <CalendarClock className="h-4 w-4" aria-hidden />
            {t("dashboard.welcomeEmpty.viewAgenda", "Ver agenda fiscal")}
          </button>
        </div>
      </CardContent>
    </Card>
  );
}
